import {
  CalculatedLine,
  Component,
  DivisionLine,
  ParallelLine,
  PerpendicularLine,
  Point2D as Point2DStruct,
  PointSlopeLine,
  PointToPointLine,
} from '@paragon/contract-library';
import { Direction2D, Line2D, Point2D } from '@paragon/geometry-library';
import { calculateLine, ExplicitCalculatedLine, findAndCalculateLine } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { snapMousePositionDuringBasicInsertion } from '../../three/hooks/useMousePositionWithBasicInsertionSnapping';
import { getBasicInsertionInformation } from '../selectors/basicInsertionSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getReferenceLineShortNamesByGuidForActiveComponent } from '../selectors/getReferenceLineShortNamesByGuidForActiveComponentSelector';
import {
  BasicInsertionSecondSelection,
  BasicLineDefinition,
  ensureIntersectionIsSerializable,
  ReferenceLineIntersection,
  setBasicInsertionFirstSelection,
} from '../slices/applicationSlice';
import {
  finishInsertingNonBraceMembers,
  finishReferenceLinesInsertion,
  getNextReferenceLineName,
} from './insertActions';
import { AsyncThunkActionVoid, ThunkActionReturn, ThunkActionVoid } from './thunkActions';

export type ReferenceLineProducer = (name: string, guid: string) => ExplicitCalculatedLine;

type Basic<T extends CalculatedLine> = Omit<T, 'guid' | 'name'>;

function toReferenceLineProducer<T extends CalculatedLine>(line: Basic<T>): ReferenceLineProducer {
  return (guid, name) => ({ ...line, guid, name }) as unknown as ExplicitCalculatedLine; // TODO: typescript
}

export function toCalculatedLines(source: Basic<CalculatedLine>[], component: Component): Line2D[] {
  return source
    .map(toReferenceLineProducer)
    .map((producer) => calculateLine(producer('', crypto.randomUUID()), component))
    .filter(notNullish);
}

function createPointToPointLine(
  line1Guid: string,
  line2Guid: string,
  endLine1Guid: string,
  endLine2Guid: string,
): Basic<PointToPointLine> {
  return {
    lineType: 'PointToPoint',
    basePointLine1Guid: line1Guid,
    basePointLine2Guid: line2Guid,
    endPointLine1Guid: endLine1Guid,
    endPointLine2Guid: endLine2Guid,
    offset: 0,
  };
}

function createPerpendicularLine(
  basePointLine1Guid: string,
  basePointLine2Guid: string,
  lineGuid: string,
): Basic<PerpendicularLine> {
  return { lineType: 'Perpendicular', basePointLine1Guid, basePointLine2Guid, lineGuid, offset: 0 };
}

function createPointSlopeLine(line1Guid: string, line2Guid: string, direction: Direction2D): Basic<PointSlopeLine> {
  return { lineType: 'PointSlope', line1Guid, line2Guid, direction, offset: 0 };
}

function createParallelLine(lineGuid: string, offset: number): Basic<ParallelLine> {
  return { lineType: 'Parallel', lineGuid, offset };
}

function createDivisionLine(line1Guid: string, line2Guid: string, divisionFactor: number): Basic<DivisionLine> {
  return { lineType: 'Division', line1Guid, line2Guid, divisionFactor };
}

export function createPreviewLinesForBasicInsertion(
  definition: BasicLineDefinition,
  secondSelection: BasicInsertionSecondSelection,
): Basic<CalculatedLine>[] | null {
  const { firstSelection, settings } = definition;
  if (firstSelection == null) return null;

  if (firstSelection.type === 'intersection' && secondSelection.type === 'intersection') {
    const { line1Guid: baseLine1Guid, line2Guid: baseLine2Guid } = firstSelection.value;
    const { line1Guid: endLine1Guid, line2Guid: endLine2Guid } = secondSelection.value;

    return [createPointToPointLine(baseLine1Guid, baseLine2Guid, endLine1Guid, endLine2Guid)];
  }

  if (firstSelection.type === 'line' && secondSelection.type === 'intersection') {
    const baseLineGuid = firstSelection.value;
    const { line1Guid: intersectionLine1Guid, line2Guid: intersectionLine2Guid } = secondSelection.value;

    return [createPerpendicularLine(intersectionLine1Guid, intersectionLine2Guid, baseLineGuid)];
  }

  if (firstSelection.type === 'line' && secondSelection.type === 'line') {
    const line1Guid = firstSelection.value;
    const line2Guid = secondSelection.value;

    const step = 1 / (settings.plurality + 1);
    const divisionFactors = Array.from({ length: settings.plurality }, (_, i) => step * (i + 1));

    return divisionFactors.map((divisionFactor) => createDivisionLine(line1Guid, line2Guid, divisionFactor));
  }

  if (firstSelection.type === 'intersection' && secondSelection.type === 'direction') {
    const { line1Guid, line2Guid } = firstSelection.value;
    const direction = Direction2D.fromObject(secondSelection.value);

    return [createPointSlopeLine(line1Guid, line2Guid, direction)];
  }

  if (firstSelection.type === 'line' && secondSelection.type === 'offset') {
    const lineGuid = firstSelection.value;
    const offset = secondSelection.value;

    return [createParallelLine(lineGuid, offset)];
  }

  return null;
}

const concludeBasicInsertion =
  (secondSelection: BasicInsertionSecondSelection, options?: { locationHint?: Point2DStruct }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const info = getBasicInsertionInformation(getState());
    if (!info.inserting || info.definition.firstSelection == null) throw new Error('Invalid state.');

    const previewLines = createPreviewLinesForBasicInsertion(info.definition, secondSelection);
    if (previewLines == null) throw new Error('Invalid state.');

    switch (info.inserting) {
      case 'member':
        await dispatch(
          finishInsertingNonBraceMembers(previewLines.map(toReferenceLineProducer), options?.locationHint),
        );

        break;

      case 'referenceLine':
        const referenceLineShortNamesByGuid = getReferenceLineShortNamesByGuidForActiveComponent(getState());
        if (!referenceLineShortNamesByGuid)
          throw new Error('Active component design or its reference line names are missing.');

        const takenNames = new Set(referenceLineShortNamesByGuid.values());

        const referenceLines = previewLines.map(toReferenceLineProducer).map((produceLine) => {
          const guid = crypto.randomUUID();
          const name = getNextReferenceLineName(takenNames);

          takenNames.add(name);
          return produceLine(guid, name);
        });

        dispatch(finishReferenceLinesInsertion(referenceLines));

        break;

      default:
        assertExhaustiveSwitch(info);
    }
  };

const setBasicInsertionEndIntersection =
  (intersection: ReferenceLineIntersection): ThunkActionVoid =>
  (dispatch, getState) => {
    const info = getBasicInsertionInformation(getState());
    if (!info.inserting || info.definition.firstSelection == null) throw new Error('Invalid state.');

    const secondSelection = {
      type: 'intersection' as const,
      value: intersection,
    };

    dispatch(concludeBasicInsertion(secondSelection, { locationHint: intersection.point }));
  };

const setBasicInsertionEndLine =
  (line: CalculatedLine, location: Point2DStruct): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const component = getActiveComponentDesign(state)?.component;

    const info = getBasicInsertionInformation(state);
    if (component == null || !info.inserting || info.definition.firstSelection == null) return;

    if (info.definition.firstSelection.type === 'line') {
      const firstLineGuid = info.definition.firstSelection.value;

      const firstLine = findAndCalculateLine(firstLineGuid, component);
      const clickedLine = findAndCalculateLine(line.guid, component);
      if (firstLine === null || clickedLine === null) return;

      // A click on the same or a coincident line creates an exact line.
      if (firstLineGuid === line.guid || firstLine.equals(clickedLine)) {
        const secondSelection = { type: 'offset' as const, value: 0 };
        dispatch(concludeBasicInsertion(secondSelection, { locationHint: location }));
        return;
      }

      // A click on a parallel line creates a division line between the two lines.
      if (firstLine.isParallelTo(clickedLine)) {
        const secondSelection = {
          type: 'line' as const,
          value: line.guid,
          clickPosition: location,
        };
        dispatch(concludeBasicInsertion(secondSelection, { locationHint: location }));
        return;
      }
    }

    // let the click event "pass through" as the user probably intended to select a nearby point in space.
    dispatch(onClickInSpaceDuringBasicInsertion(location));
  };

export const onClickIntersectionDuringBasicInsertion =
  (intersection: ReferenceLineIntersection): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const info = getBasicInsertionInformation(getState());
    if (!info.inserting) throw new Error('Basic insertion handler called while basic insertion not active.');

    if (info.definition.firstSelection == null) {
      dispatch(
        setBasicInsertionFirstSelection({
          type: 'intersection',
          value: ensureIntersectionIsSerializable(intersection),
        }),
      );
    } else {
      dispatch(setBasicInsertionEndIntersection(intersection));
    }
  };

export const onClickInSpaceDuringBasicInsertion =
  (location: Point2DStruct): ThunkActionReturn<false | void> =>
  (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;

    // This must occur during basic insertion when the first selection has been made.
    if (!(
      (insertingObjectInfo?.type === 'member' || insertingObjectInfo?.type === 'referenceLine') &&
      insertingObjectInfo.basicDefinition?.firstSelection != null
    )) {
      return; // don't stop propagation
    }

    const { firstSelection: base } = insertingObjectInfo.basicDefinition;

    const snappedClickPosition = snapMousePositionDuringBasicInsertion(
      Point2D.fromObject(location),
      getActiveComponentDesign(state)!.component,
      getBasicInsertionInformation(state),
      null, // it's safe to assume there's no hovered intersection because we know this is a "click in space"
    )!;

    switch (base.type) {
      case 'intersection': {
        // With an intersection base, a click in space creates a point-slope line, so calculate the direction.
        const direction = Direction2D.fromPointObjects(base.value.point, snappedClickPosition).toObject();
        const secondSelection = {
          type: 'direction' as const,
          value: direction,
        };
        dispatch(concludeBasicInsertion(secondSelection, { locationHint: snappedClickPosition }));
        break;
      }

      case 'line': {
        // With a line base, a click in space creates a parallel line, so calculate the offset.

        const activeComponent = getActiveComponentDesign(state)?.component;
        if (activeComponent == null) return;

        const baseLineGeometry = findAndCalculateLine(base.value, activeComponent);
        if (baseLineGeometry == null) return;

        const angleLine = Line2D.fromPoints(baseLineGeometry.basePoint, snappedClickPosition);
        const angle = baseLineGeometry.direction.angleTo(angleLine.direction);

        const sign = angle >= 0 ? 1 : -1;
        const offset = snappedClickPosition.distanceToLine(baseLineGeometry) * sign;

        const secondSelection = { type: 'offset' as const, value: offset };
        dispatch(concludeBasicInsertion(secondSelection, { locationHint: snappedClickPosition }));
        break;
      }

      default:
        assertExhaustiveSwitch(base);
    }

    return false; // stop propagation
  };

export const onClickReferenceLineDuringBasicInsertion =
  (line: CalculatedLine, location: Point2DStruct): ThunkActionVoid =>
  (dispatch, getState) => {
    const info = getBasicInsertionInformation(getState());
    if (!info.inserting) return;

    if (info.definition.firstSelection == null) {
      dispatch(
        setBasicInsertionFirstSelection({
          type: 'line',
          value: line.guid,
        }),
      );
    } else {
      dispatch(setBasicInsertionEndLine(line, location));
    }
  };
